import {useCallback, useEffect, useState, useRef, useMemo, memo} from "react";
import { useDispatch } from "react-redux";
import reduxUtil from "@plannel/utils/redux-util";
import { Backdrop, Box, Checkbox, CircularProgress, MenuItem, InputLabel, Select, FormControl, FormControlLabel, Slider, TextField, Typography} from "@mui/material";
import { withTranslation } from "react-i18next";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import CheckIcon from "@mui/icons-material/Check";

import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import { AgGridReact } from "ag-grid-react";
import DataState from "@plannel/components/aggrid/DataState";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import i18n from "@plannel/utils/i18n";
import NumberUtil from "@plannel/utils/number-util";
import codeUtil from "@plannel/pages/utils/code-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import AbcXyzChip from "@plannel/components/aggrid/AbcXyzChip";
import HeatMap from "@plannel/components/AbcHeatMap";
import Snackbar from "@plannel/components/Snackbar";
import debug from "@plannel/utils/debug-util";
import bfTargetService from "@plannel/services/data/bf-target-service";
import planConfigService from "@plannel/services/data/plan-config-service";
import hrchyConfigService from "@plannel/services/data/hrchy-config-service";
import userService from "@plannel/services/system/user-service";
import Dialog from "@plannel/components/Dialog";

import { SaveButton } from "@plannel/components/ActionIconButton";
import { cloneDeep, entries, indexOf, isEqual, size, isEmpty, includes } from "lodash";

import "@plannel/scss/components/AbcXyzAnalysis.scss";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import { FS } from "@plannel/utils/font-util";

const d3 = require("d3");
const { getCurrencySymbol, classifiBaseCode } = codeUtil;
const {REVENUE, QUANTITY, FREQUENCY, PROFIT, COGS} = classifiBaseCode;

const CURRENT_DATE = new ZDate();
const INITIAL_END_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() + 1, 0);
const DEFAULT_ACTIVE = "CZ";

const AbcXyzContentArea = memo(({ data, currencyCd, type, userData, ITEM, t, abcxyz, axClick }) => {

  const chartDatas = data;
  const [abcXyzData, setAbcXyzData] = useState({});
  const [yAxisName, setYAxisName] = useState("");

  const volumes = ["A", "B", "C"];
  const uncertainties = ["X", "Y", "Z"];

  useEffect(() => {
    if (Object.keys(chartDatas).length > 0) {
      if (yAxisName === "") {
        setYAxisName(GridUtils.gridValueL10N(chartDatas[0]?.baseCd));
      }
      const result = {
        AX: chartDatas.AX.length,
        AY: chartDatas.AY.length,
        AZ: chartDatas.AZ.length,
        BX: chartDatas.BX.length,
        BY: chartDatas.BY.length,
        BZ: chartDatas.BZ.length,
        CX: chartDatas.CX.length,
        CY: chartDatas.CY.length,
        CZ: chartDatas.CZ.length,
        AX_TOTAL: chartDatas.AX.reduce((accumulator, object) => {return accumulator + object.total}, 0),
        BX_TOTAL: chartDatas.BX.reduce((accumulator, object) => {return accumulator + object.total}, 0),
        CX_TOTAL: chartDatas.CX.reduce((accumulator, object) => {return accumulator + object.total}, 0),
        AY_TOTAL: chartDatas.AY.reduce((accumulator, object) => {return accumulator + object.total}, 0),
        BY_TOTAL: chartDatas.BY.reduce((accumulator, object) => {return accumulator + object.total}, 0),
        CY_TOTAL: chartDatas.CY.reduce((accumulator, object) => {return accumulator + object.total}, 0),
        AZ_TOTAL: chartDatas.AZ.reduce((accumulator, object) => {return accumulator + object.total}, 0),
        BZ_TOTAL: chartDatas.BZ.reduce((accumulator, object) => {return accumulator + object.total}, 0),
        CZ_TOTAL: chartDatas.CZ.reduce((accumulator, object) => {return accumulator + object.total}, 0),
        TOTAL: 0,
        VALUETOTAL : 0
      };
      result.A = result.AX + result.AY + result.AZ;
      result.B = result.BX + result.BY + result.BZ;
      result.C = result.CX + result.CY + result.CZ;
      result.X = result.AX + result.BX + result.CX;
      result.Y = result.AY + result.BY + result.CY;
      result.Z = result.AZ + result.BZ + result.CZ;

      result.TOTAL = result.A + result.B + result.C;
      result.VALUETOTAL = result.AX_TOTAL + result.BX_TOTAL + result.CX_TOTAL + result.AY_TOTAL + result.BY_TOTAL + result.CY_TOTAL + result.AZ_TOTAL + result.BZ_TOTAL + result.CZ_TOTAL;
      setAbcXyzData(result);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartDatas]);

  const chartValueFormatter = (value, isCurrency = false) => {
    const expression = currencyCd.current;
    const symbol = getCurrencySymbol(expression) ?? userData?.currencySymbol ?? "";
    if (isCurrency && (type === REVENUE || type === PROFIT || type === COGS)) {
      const formattedValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 2});
      return symbol + formattedValue
    } else {
      return NumberUtil.formatNumber(value, 0);
    }
  };

  const chartPercentageFormatter = value => {
    return NumberUtil.formatNumber(value, { style: "percent", maximumFractionDigits: 2 });
  };

  const percentageCalc = paramValue => {
    const total = abcXyzData?.TOTAL ? abcXyzData.TOTAL : 0;
    const value = paramValue && total !== 0 ? paramValue / total : 0;

    return chartPercentageFormatter(value);
  };

  const AbcXyzYAxis = useCallback(() => {
    const valueA = abcXyzData?.A ? abcXyzData.A : 0;
    const valueB = abcXyzData?.B ? abcXyzData.B : 0;
    const valueC = abcXyzData?.C ? abcXyzData.C : 0;

    return (
      <Box sx={{ width: "100%", height: "100%", display: "inline-flex" }}>
        <Box sx={{ width: "100%", height: "100%", display: "inline-block" }}>
          <Box className="vertical-wrapper default-wh content-center">
            <Box sx={{ display: "inline-block" }}>
              <Box
                className="content-center"
                sx={{ width: "100%", height: "auto", fontSize: FS.KPI, fontWeight: "500" }}>
                A
              </Box>
              <Box
                className="content-center"
                sx={{ width: "100%", height: "auto", fontSize: FS.TITLE }}>
                {chartValueFormatter(valueA)}
              </Box>
              <Box
                className="content-center"
                sx={{ width: "100%", height: "auto", fontSize: FS.TITLE }}>{`(${percentageCalc(valueA)})`}</Box>
            </Box>
          </Box>
          <Box className="vertical-wrapper default-wh content-center">
            <Box sx={{ display: "inline-block" }}>
              <Box
                className="content-center"
                sx={{ width: "100%", height: "auto", fontSize: FS.KPI, fontWeight: "500" }}>
                B
              </Box>
              <Box
                className="content-center"
                sx={{ width: "100%", height: "auto", fontSize: FS.TITLE }}>
                {chartValueFormatter(valueB)}
              </Box>
              <Box
                className="content-center"
                sx={{ width: "100%", height: "auto", fontSize: FS.TITLE }}>{`(${percentageCalc(valueB)})`}</Box>
            </Box>
          </Box>
          <Box className="vertical-wrapper default-wh content-center">
            <Box sx={{ display: "inline-block" }}>
              <Box
                className="content-center"
                sx={{ width: "100%", height: "auto", fontSize: FS.KPI, fontWeight: "500" }}>
                C
              </Box>
              <Box
                className="content-center"
                sx={{ width: "100%", height: "auto", fontSize: FS.TITLE }}>
                {chartValueFormatter(valueC)}
              </Box>
              <Box
                className="content-center"
                sx={{ width: "100%", height: "auto", fontSize: FS.TITLE }}>{`(${percentageCalc(valueC)})`}</Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abcXyzData, yAxisName]);

  const AbcXyzXAxis = useCallback(() => {
    const valueX = abcXyzData?.X ? abcXyzData.X : 0;
    const valueY = abcXyzData?.Y ? abcXyzData.Y : 0;
    const valueZ = abcXyzData?.Z ? abcXyzData.Z : 0;

    return (
      <Box
        className="default-wh"
        sx={{ mt: 0.5 }}>
        <Box className="default-wh display-if">
          <Box className="default-wh">
            <Box
              className="class-box-wrapper"
              sx={{ height: "100% !important", textAlign: "center" }}>
              <Box
                className="horizon-wrapper default-wh"
                sx={{ display: "inline-block" }}>
                <Box sx={{ display: "inline-block", width: "100%", fontSize: FS.KPI, fontWeight: "500" }}>X</Box>
                <Box sx={{ display: "inline-block", width: "100%", fontSize: FS.TITLE }}>
                  {chartValueFormatter(valueX)} {`(${percentageCalc(valueX)})`}
                </Box>
              </Box>
              <Box
                className="horizon-wrapper default-wh"
                sx={{ display: "inline-block" }}>
                <Box sx={{ width: "100%", fontSize: FS.KPI, fontWeight: "500" }}>Y</Box>
                <Box sx={{ width: "100%", fontSize: FS.TITLE }}>
                  {chartValueFormatter(valueY)} {`(${percentageCalc(valueY)})`}
                </Box>
              </Box>
              <Box
                className="horizon-wrapper default-wh"
                sx={{ display: "inline-block" }}>
                <Box sx={{ width: "100%", fontSize: FS.KPI, fontWeight: "500" }}>Z</Box>
                <Box sx={{ width: "100%", fontSize: FS.TITLE }}>
                  {chartValueFormatter(valueZ)} {`(${percentageCalc(valueZ)})`}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box
          className="display-if content-center"
          sx={{ width: "100%", height: "auto" }}>
          <Box sx={{ textAlign: "center", fontWeight: "bold", width: "50%", height: "100%" }}></Box>
        </Box>
      </Box>
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abcXyzData]);

  const MakeAbcXyzChartBox = ({ abcXyzChartData }) => {
    const MakeBox = ({ volume, uncertainty }) => {
      const chartClass = `${volume}${uncertainty}`;
      const alignClassName = "horizon-wrapper";

      const value = abcXyzChartData?.[chartClass];
      const totalName = `${chartClass}_TOTAL`;
      const totalValue = abcXyzChartData?.[totalName];
      const classInfo = codeUtil.ABCClassConfig[chartClass];

      const className = classInfo?.className ? classInfo.className : "";

      const itemOrItems = value > 1 ? "items" : ITEM ;
      const chartValue = value || value === 0 ? `${chartValueFormatter(value)} ${t(itemOrItems)}` : `0 ${t(itemOrItems)}`;
      let chartValuePercentage = 0;
      if (value || value === 0) {
        chartValuePercentage = abcXyzChartData.TOTAL !== 0 ? Number(value / abcXyzChartData.TOTAL) : 0;
      }
      chartValuePercentage = chartPercentageFormatter(chartValuePercentage);
      let chartTotalValuePercentage = 0;
      if (totalValue || totalValue === 0) {
        chartTotalValuePercentage = abcXyzChartData.VALUETOTAL !== 0 ? Number(totalValue / abcXyzChartData.VALUETOTAL) : 0;
      }
      chartTotalValuePercentage = chartPercentageFormatter(chartTotalValuePercentage);
      const keyValue = `${chartClass}${value || value === 0 ? value : "_none"}`;
      const checkClass = abcxyz.includes(chartClass) ? `active ${chartClass}-check abc-check` : `${chartClass}-check abc-check`;
      const activeClass = abcxyz.includes(chartClass) ? `active ${chartClass}-chart` : `abxBox ${chartClass}-chart`;

      return (
        <Box
          key={keyValue}
          className={`${alignClassName} abc-xyz-chart ${className} ${activeClass}`}
          onClick={e => axClick(e, chartClass)}>
          <CheckIcon
            className={checkClass}
            size="small"
            sx={{ position: "absolute", top: 5, left: 5, fontSize: FS.DISPLAY }}
          />
          <Box sx={{width: "100%", height: "100%", display: "inline-flex", alignItems: "center", justifyContent: "center"}}>
            <Typography sx={{ fontSize: FS.H1 }}>
              {
                <>
                  {chartValue} {`(${chartValuePercentage})`}
                  <br />{chartValueFormatter(totalValue, true)} {`(${chartTotalValuePercentage})`}
                </>
              }
            </Typography>
          </Box>
        </Box>
      );
    };

    const MakeBoxCombined = ({ volume }) => {
      const className = "vertical-wrapper";
      return (
        <Box className={className}>
          {uncertainties.map(uncertainty => {
            return (
              <MakeBox
                key={`${volume}${uncertainty}`}
                volume={volume}
                uncertainty={uncertainty}
              />
            );
          })}
        </Box>
      );
    };

    return volumes.map(volume => {
      return (
        <MakeBoxCombined
          key={volume}
          volume={volume}
        />
      );
    });
  };

  return (
    <Box sx={{ width: "100%", height: "100%", overflow: "auto", overflowX: "hidden" }}>
      <Box sx={{ width: "100%", minHeight: "350px", height: "calc(100% - 10px)" }}>
        <Box className="default-wh" sx={{ display: "inline-block" }}>
          <Box sx={{ display: "inline-flex", width: "100%", height: "calc(100% - 60px)" }}>
            <Box sx={{ minWidth: "120px", width: 80 }}>
              <AbcXyzYAxis />
            </Box>
            <Box sx={{ width: "calc(100% - 60px)", height: "100%", display: "inline-block" }}>
              <MakeAbcXyzChartBox abcXyzChartData={abcXyzData} />
            </Box>
          </Box>
          <Box
            sx={{
              width: "100%",
              height: "65px",
              overflow: "hidden",
              pl: "120px",
              boxSizing: "border-box",
            }}>
            <AbcXyzXAxis />
          </Box>
        </Box>
      </Box>
    </Box>
  );
},(prevProps, nextProps) => {
  return (
    prevProps.data === nextProps.data &&
    prevProps.abcxyz === nextProps.abcxyz &&
    prevProps.type === nextProps.type &&
    prevProps.currencyCd === nextProps.currencyCd &&
    prevProps.userData === nextProps.userData &&
    prevProps.ITEM === nextProps.ITEM &&
    prevProps.t === nextProps.t &&
    prevProps.axClick === nextProps.axClick
  );
  }
);


const Q_LEGEND = [95, 90, 85, 80, 75, 70, 65, 60, 55, 50, 45, 40, 35, 30, 25, 20, 15, 10, 5, 0];

const TargetItemSelection = ({ t, title, viewName }) => {
  const reduxViewState = cloneDeep(reduxUtil.getViewState(viewName));
  const reduxDispatch = useDispatch();

  const userData = JSON.parse(localStorage.getItem("user"));
  const [userRoles, setUserRoles] = useState(reduxViewState?.userRoles ?? []);

  const gridRef = useRef();
  const currencyCd = useRef("JPY");
  const [gridLoading, setGridLoading] = useState(null);
  const [selectAll, setSelectAll] = useState(false);
  const [selectDpAll, setSelectDpAll] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [finishSave, setFinishSave] = useState(false);
  const [reload, setReload] = useState(false)

  const codes = codeUtil.dpABCConfig;
  const { roles } = codeUtil;
  const ITEM = "item";
  const interval = 0.05;
  const defaultCv = 2.0;

  const [abcxyz, setABCXYZ] = useState(reduxViewState?.abcxyz ?? []);
  const [abcValues, setAbcValues] = useState(reduxViewState?.abcValues ?? [-85, -70]);
  const [xyzValues, setXyzValues] = useState(reduxViewState?.xyzValues ?? [0.5, 1.5]);
  const abcXyzValues = useRef({abcValues: reduxViewState?.abcValues ?? [-85, -70], xyzValues: reduxViewState?.xyzValues ?? [0.5, 1.5]})
  const [isAllChecked, setIsAllChecked] = useState(reduxViewState?.isAllChecked ?? false);
  const [abcXyzCnt, setAbcXyzCnt] = useState(reduxViewState?.abcXyzCnt ?? {});

  const [gradeA, setGradeA] = useState(reduxViewState?.gradeA ?? 70);
  const [gradeB, setGradeB] = useState(reduxViewState?.gradeB ?? 85);
  const [gradeX, setGradeX] = useState(reduxViewState?.gradeX ?? 0.5);
  const [gradeY, setGradeY] = useState(reduxViewState?.gradeY ?? 1.5);

  const [startDate, setStartDate] = useState(reduxViewState?.startDate ?? "");
  const [endDate, setEndDate] = useState(reduxViewState?.endDate ?? INITIAL_END_DATE);
  const [startMinDate, setStartMinDate] = useState(reduxViewState?.endDate ?? INITIAL_END_DATE)
  const [resultData, setResultData] = useState({});
  const [heatMapData, setHeatMapData] = useState([]);
  const [mapItemData, setMapItemData] = useState([]);
  const [gridDatas, setGridDatas] = useState([]);
  const [allData, setAllData] = useState([]);
  const [abcConfig, setAbcConfig] = useState({});
  const [type, setType] = useState(reduxViewState?.type ?? REVENUE);
  const [maxCv, setMaxCv] = useState(reduxViewState?.maxCv ?? null);
  const { module, dpConfig, displayDigit} = codeUtil;
  const [custLevel, setCustLevel] = useState(reduxViewState?.custLevel ?? {})
  const [custLevelDatas, setCustLevelDatas] = useState(reduxViewState?.custLevelDatas ?? [])
  const { DP } = module;
  const { ABC_XYZ } = dpConfig;
  const digit = displayDigit;
  const calculateDigit = 5;
  const minDateYear = 9;
  const { snackBarMessageColor } = ColorUtil;
  const bfUpdate = useRef(new Map());
  const dpUpdate = useRef(new Map());

  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const defaultGridMemo = useMemo(() => {
    const defaultSettings = DefaultGridSetting({ title, viewName });
    defaultSettings.defaultColDef = {
      ...defaultSettings.defaultColDef,
      flex: 1,
      minWidth: 30,
      resizable: true,
    }
    return defaultSettings;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 뷰 등록은 **마운트 시 한 번**만. 렌더 본문에서 dispatch 하면 렌더 도중 store 가 바뀌어
  // App 이 재렌더되고, React 가 "Cannot update a component (`App`) while rendering a different
  // component (`TargetItemSelection`)" 경고를 낸다(렌더→dispatch→렌더 루프).
  // createObjectState(viewName) 는 { viewName } 만 만드는 순수 함수라 매번 부를 이유가 없다.
  useEffect(() => {
    reduxDispatch(reduxUtil.updateViewState({ ...reduxUtil.createObjectState(viewName) }));
  }, [viewName, reduxDispatch]);

  const onBtShowLoading = useCallback(() => {
    gridRef?.current?.api?.setGridOption('loading', true);
  }, [])

  const onBtHide = useCallback(() => {
    gridRef?.current?.api?.setGridOption('loading', false);
  }, [])

  const gradeAbcChange = (_e, newValue) => {
    setAbcValues(newValue);
  };

  const gradeAbcChangeCommitted = (_e, newValue) => {
    const minDistance = 5;
    let newValueA = 0;
    let newValueB = 0;
    const newInterval = [0, 0];

    if (Math.abs(gradeB) > Math.abs(newValue[0]) && newValue[0] === newValue[1]) {
      newValueA = Math.abs(newValue[1]);
      newValueB = Math.abs(Math.min(newValue[0], newValue[1] - minDistance));
      newInterval[0] = Math.min(newValue[0], newValue[1] - minDistance)
      newInterval[1] = newValue[1]
    } else if (Math.abs(gradeA) < Math.abs(newValue[1]) && newValue[0] === newValue[1]) {
      newValueA = Math.abs(Math.max(newValue[1], newValue[0] + minDistance));
      newValueB = Math.abs(newValue[0])
      newInterval[0] = newValue[0]
      newInterval[1] = Math.max(newValue[1], newValue[0] + minDistance)
    } else {
      newValueA = Math.abs(newValue[1])
      newValueB = Math.abs(newValue[0])
      newInterval[0] = newValue[0]
      newInterval[1] = newValue[1]
    }
    setAbcValues(newInterval);
    abcXyzValues.current = {...abcXyzValues.current, abcValues: newInterval};
    setGradeA(newValueA);
    setGradeB(newValueB);

    const currentState = reduxUtil.getViewState(viewName) || reduxUtil.createObjectState(viewName);
    reduxDispatch(
      reduxUtil.updateViewState({
        ...currentState,
        gradeA: newValueA,
        gradeB: newValueB,
        abcValues: newInterval,
      })
    );

  };

  const gradeXyzChange = (_e, newValue) => {
    setXyzValues(newValue);
  };

  const gradeXyzChangeCommitted = (_e, newValue) => {
    const minDistance = interval;

    let newValueX = 0.0;
    let newValueY = 0.0;

    if (xyzValues[0] < newValue[0] && newValue[0] === newValue[1]) {
      newValueX = parseFloat(Math.min(newValue[0], newValue[1] - minDistance).toFixed(digit));
      newValueY = newValue[1];
    } else if (xyzValues[1] > newValue[1] && newValue[0] === newValue[1]) {
      newValueX = newValue[0];
      newValueY = parseFloat(Math.max(newValue[1], newValue[0] + minDistance).toFixed(digit));
    } else {
      newValueX = parseFloat(Math.min(newValue[0], gradeY - interval).toFixed(digit));
      newValueY = parseFloat(Math.max(newValue[1], gradeX + interval).toFixed(digit));
    }

    abcXyzValues.current = {...abcXyzValues.current, xyzValues: [newValueX, newValueY]};
    setXyzValues([newValueX, newValueY]);
    setGradeX(newValueX);
    setGradeY(newValueY);

    const currentState = reduxUtil.getViewState(viewName) || reduxUtil.createObjectState(viewName);
    reduxDispatch(
      reduxUtil.updateViewState({
        ...currentState,
        gradeX: newValueX,
        gradeY: newValueY,
        xyzValues: [newValueX, newValueY],
      })
    );
  };

  const setSelectItemData = selectAbc => {
    let addData = [];
    if (selectAbc.length > 0 && Object.keys(abcXyzCnt).length > 0) {
      for (const row of selectAbc) {
        addData = addData.concat(abcXyzCnt[row]);
      }
      for (const row of addData) {
        row.initialFlg = row.bfFlg;
        row.initialdpFlg = row.dpFlg;
      }
      addData.sort((a, b) => b.ratio - a.ratio);
      setGridDatas(addData);
    }
  };

  const changeActive = param => {
    const classNmList = [`${param}-chart`, `${param}-check`]; //, `${param}-chip-select`

    const setClassList = isAdd => {
      for (const eleClass of classNmList) {
        const el = document.getElementsByClassName(eleClass);
        if (el) {
          if (isAdd) {
            el[0].classList.add("active");
          } else {
            el[0].classList.remove("active");
          }
        }
      }
    };

    const cloneAbcXyz = cloneDeep(abcxyz);
    if (cloneAbcXyz.includes(param)) {
      const dIdx = cloneAbcXyz.indexOf(param);
      cloneAbcXyz.splice(dIdx, 1);
      setClassList(false);
    } else {
      setClassList(true);
      cloneAbcXyz.push(param);
    }

    setIsAllChecked(isEqual(size(cloneAbcXyz), size(codes)));
    setABCXYZ(cloneAbcXyz);
    setSelectItemData(cloneAbcXyz);

    const currentState = reduxUtil.getViewState(viewName) || reduxUtil.createObjectState(viewName);
    reduxDispatch(
      reduxUtil.updateViewState({
        ...currentState,
        isAllChecked: isEqual(size(cloneAbcXyz), size(codes)),
        abcxyz: cloneAbcXyz,
      })
    );



  };

  const axClick = (_e, param) => {
    changeActive(param);
    d3.selectAll("#selected").remove();
  };

  const CustomBooleanIcon = activeProps => {
    const { displayName, selectAll: select, setSelectAll: setSelect } = activeProps;
    const allCheckActive = e => {
      const allActive = e?.target?.checked;
      setSelect(allActive);
    };

    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box>
          <input
            type="checkbox"
            defaultChecked={select}
            onClick={e => allCheckActive(e)}
          />
        </Box>
        <Box className="customHeaderLabel">{displayName}</Box>
      </Box>
    );
  };

  const CustomBooleanDpIcon = activeProps => {
    const { displayName, selectDpAll: select, setSelectDpAll: setSelect } = activeProps;
    const allCheckActive = e => {
      const allActive = e?.target?.checked;
      setSelect(allActive);
    };

    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box>
          <input
            type="checkbox"
            defaultChecked={select}
            onClick={e => allCheckActive(e)}
          />
        </Box>
        <Box className="customHeaderLabel">{displayName}</Box>
      </Box>
    );
  };

  const upperCaseFormatter = params => {
    return params.value.toUpperCase();
  };

  const checkFormatter = params => {
    const checkActive = (event) => {
      if (params.column.colId === "bfFlg") {
        params.node.data.bfFlg = event?.target?.checked;
      } else if (params.column.colId === "dpFlg") {
        params.node.data.dpFlg = event?.target?.checked;
      }

      if (params.column.colId === "bfFlg" && event?.target?.checked !== params.data.initialFlg) {
        params.node.rowState = 'updated';
        DataState.setDataState(params.node);
        bfUpdate.current.set(params.data.custHrchyCd+params.data.itemCd, {
          ...params.data
        });
      } else if (params.column.colId === "dpFlg" && event?.target?.checked !== params.data.initialdpFlg) {
        params.node.rowState = 'updated';
        DataState.setDataState(params.node)
        dpUpdate.current.set(params.data.custHrchyCd+params.data.itemCd, {
          ...params.data
        });
      } else {
        delete params.node.rowState;
        DataState.setDataState(params.node);
      }
    }

    return (
      <Box sx={{ mt: 0.3}}>
      <input
        type="checkbox"
        defaultChecked={params.value}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => checkActive(e)}
      />
    </Box>
    )
  };

  const decimalPointFormatter = params => {
    return params.value.toFixed(digit)
  };

  const amtPointFormatter = params => {
    const expression = currencyCd.current;
    const symbol = getCurrencySymbol(expression) ?? userData?.currencySymbol ?? "";
    if (type === REVENUE || type === PROFIT || type === COGS) {
      return symbol + NumberUtil.formatNumber(params.value, {maximumFractionDigits: 2});
    } else {
      return NumberUtil.formatNumber(params.value, 0);
    }
  };

  const bfColDef = [{
    headerName: "BF",
    field: "bfFlg",
    maxWidth: 65,
    width: 65,
    cellRenderer: checkFormatter,
    type: ["booleanColumn"],
    pinned: "left", lockPinned: true,
    editable: false,
    headerComponent: CustomBooleanIcon,
    headerComponentParams: {
      enableMenu: false,
      setSelectAll,
      selectAll,
    },
  },
  {
    headerName: "initialFlg",
    field: "initialFlg",
    hide: true,
    suppressColumnsToolPanel: true,
    maxWidth: 45,
    width: 45,
    type: ["booleanColumn"],
  }];

  const colDefs = [
    {
      headerName: "DP",
      field: "dpFlg",
      maxWidth: 65,
      width: 65,
      cellRenderer: checkFormatter,
      type: ["booleanColumn"],
      pinned: "left", lockPinned: true,
      editable: false,
      headerComponent: CustomBooleanDpIcon,
      headerComponentParams: {
        enableMenu: false,
        setSelectDpAll,
        selectDpAll,
      },
    },
    {
      headerName: "initialdpFlg",
      field: "initialdpFlg",
      hide: true,
      suppressColumnsToolPanel: true,
      maxWidth: 45,
      width: 45,
      type: ["booleanColumn"],
    },
    {
      headerName: "itemCd",
      field: "itemCd",
      width: 300,
      editable: false,
      filter: 'agTextColumnFilter'
    },
    {
      headerName: "itemName",
      field: "name",
      editable: false,
      width: 300,
      filter: 'agTextColumnFilter'
    },
    {
      headerName: "customerHrchyCd",
      field: "custHrchyCd",
      width: 300,
      editable: false,
      filter: 'agTextColumnFilter'
    },
    {
      headerName: "customerHrchyName",
      field: "custHrchyName",
      editable: false,
      width: 300,
      filter: 'agTextColumnFilter'
    },
    {
      ...GridUtils.composedHeader(() => `${i18n.t(type)} (%)`),
      field: "ratio",
      editable: false,
      type: ["detailPercentColumn", "nonEditableColumn", "rightAligned"],
      filter: 'agNumberColumnFilter',
      width: 100,
    },
    {
      headerName: type,
      field: "total",
      editable: false,
      valueFormatter: amtPointFormatter,
      filter: 'agNumberColumnFilter',
      width: 100,
      type: ["rightAligned"]
    },
    {
      headerName: "cv",
      field: "cv",
      editable: false,
      valueFormatter: decimalPointFormatter,
      filter: 'agNumberColumnFilter',
      width: 100,
      type: ["rightAligned"]
    },
    {
      headerName: "abcXyz",
      field: "grade",
      editable: false,
      valueFormatter: upperCaseFormatter,
      filter: 'agTextColumnFilter',
      width: 100,
      cellRenderer: AbcXyzChip
    },
    {
      headerName: "lastSoldDate",
      field: "lastSalesDate",
      editable: false,
      filter: 'agTextColumnFilter',
      width: 100,
    }
  ];

  const clearAbcChart = useCallback(() => {
    setABCXYZ([]);
    setIsAllChecked(false);

    const currentState = reduxUtil.getViewState(viewName) || reduxUtil.createObjectState(viewName);
    reduxDispatch(
      reduxUtil.updateViewState({
        ...currentState,
        abcxyz: [],
        isAllChecked: false,
      })
    );
  }, [viewName, reduxDispatch]);

  const initialPlanConfig = () => {
    let isConfig = false;
    const param = {
      moduleCd: DP,
      activeFlg: true,
      configCd: ABC_XYZ
    };
    planConfigService
      .getConfigCd(param)
      .then(response => {
        if (response.data) {
          setAbcConfig(response.data);
          isConfig = true;
        }
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        if (!reduxViewState?.startDate && !isConfig) {
          setEndDate(dateUtils.endOfMonthDate(endDate));
          const date = new ZDate(new ZDate(endDate).setFullYear(new ZDate(endDate).getFullYear() - 1));
          setStartDate(
            dateUtils.startOfMonthDate(
              new ZDate(date.setDate(date.getDate() + 1))
            )
          );
          setMaxCv(defaultCv);
          setIsLoading(true);
        }
        else {
          if (reduxViewState?.startDate) {
            setIsLoading(false);
          }
        }
      });
  };

  const getItemOnHeatMap = useCallback((props) => {
    if (!props?.length || !mapItemData?.length) {
      return;
    }

    const reverseQ = cloneDeep(Q_LEGEND).reverse();
    const gridItemList = [];

    for (const selectH of props) {
      const pct = indexOf(reverseQ, selectH.pct, 0);
      const selectItems = mapItemData[pct][selectH.cv_lbl];
      gridItemList.push(...selectItems);
    }

    const sorted = gridItemList
      .map(item => ({ cv: item.cv, ratio: item.ratio, ...item.item }))
      .sort((a, b) => b.ratio - a.ratio);

    const hasGridDataChanged = (prev, next) => {
      if (prev.length !== next.length) return true;
      return prev.some((item, idx) => !isEqual(item, next[idx]));
    };

    setGridDatas(prevGridDatas => {
      return hasGridDataChanged(prevGridDatas, sorted) ? sorted : prevGridDatas;
    });

    clearAbcChart();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapItemData, clearAbcChart]);

  const setMapData = resDatas => {
    const mapData = [];
    if (resDatas?.length > 0) {
      for (const [idx, resData] of entries(resDatas)) {
        const pctObj = { pct: Q_LEGEND[idx], cnt: 0 };
        for (const [cv, values] of entries(resData)) {
          pctObj[cv] = values.length;
        }
        mapData.push(pctObj);
      }
    }
    setHeatMapData(mapData);
  };

  const makeMapByPct = () => {
    const pct = {};
    for (let i = 0; i < 100; i = i + 5) {
      pct[i] = [];
    }
    return pct;
  };

  const makeMapByCv = () => {
    const cv = {};

    for (let i = 0.0; i <= maxCv - 0.04; i = i + interval) {
      cv[Number(i).toFixed(digit)] = [];
    }
    return cv;
  };

  const makeHeatMapData = data => {
    const heatMapResult = makeMapByPct();
    const ordered = Object.keys(heatMapResult)
      .sort()
      .reduce((obj, key) => {
        obj[key] = heatMapResult[key];
        return obj;
      }, {});
    for (const hrchy of Object.keys(data)) {
      const itemValues = data[hrchy];
      let beforeAccu = 0;
      let totalSum = 0;
      for (const row of itemValues) {
        beforeAccu = totalSum / row.totalSum;
        if (row.ratio <= 0) {
          const list = ordered[Math.floor(95 / 5) * 5];
          ordered[Math.floor(95 / 5) * 5] = [...list, row];
        } else {
          totalSum = Number(row.total + totalSum);
          if (beforeAccu >= 1) {
            const list = ordered[Math.floor(95 / 5) * 5];
            ordered[Math.floor(95 / 5) * 5] = [...list, row];
          } else {
            const list = ordered[Math.floor(beforeAccu * 100 / 5) * 5];
            ordered[Math.floor(beforeAccu * 100/ 5) * 5] = [...list, row];
          }
        }
      }
    }

    const heatMap = [];
    for (const pct of Object.keys(ordered)) {
      const cvData = makeMapByCv();
      for (const dto of ordered[pct]) {
        let mapKey = Number(((Math.floor(dto.cv * 100).toFixed(digit)) - (Math.floor(dto.cv * 100).toFixed(digit) % 5)) / 100).toFixed(digit);
        if (mapKey <= 0.0) {
          mapKey = Number(Math.floor(0).toFixed(digit) / 100).toFixed(digit);
        } else if (Number(mapKey) >= maxCv) {
          mapKey = (maxCv-interval).toFixed(digit);
        }

        const list = cvData[mapKey];
        cvData[mapKey] = [...list, dto];
      }
      heatMap.push(cvData);
    }
    setMapData(heatMap.reverse());
    setMapItemData(heatMap.reverse());
  };

  const computeAbcXyz = data => {
    if (!isEmpty(abcXyzValues.current)) {
      const xyzVal = abcXyzValues.current.xyzValues;
      const abcVal = abcXyzValues.current.abcValues;

      const xValue = xyzVal[0];
      const yValue = xyzVal[1];
      const allClass = [];
      const aValue = -abcVal[1] / 100;
      const bValue = -abcVal[0]  / 100;

      const axClass = [];
      const ayClass = [];
      const azClass = [];
      const bxClass = [];
      const byClass = [];
      const bzClass = [];
      const cxClass = [];
      const cyClass = [];
      const czClass = [];

      for (const key of Object.keys(data)) {
        let count = 0;
        let beforeValueTotal = 0.0;
        let totalSum = 0;
        let abc = "";
        let beforetotalAccu = 0;
        const itemData = data[key];
        for (const row of itemData) {
          let grade = "";
          beforetotalAccu = Number((totalSum / row.totalSum).toFixed(calculateDigit));
          totalSum = Number(row.total + totalSum);
          row.accumulationTotal = totalSum;
          row.totalAccuRatio = Number((row.accumulationTotal / row.totalSum).toFixed(calculateDigit));
          if (row.total !== beforeValueTotal) {
            count++;
            if (row.total <= 0) {
              abc = "c";
            } else {
              if (beforetotalAccu <= aValue) {
                abc = "a";
              } else if (beforetotalAccu > aValue && beforetotalAccu <= bValue) {
                abc = "b";
              } else if (beforetotalAccu > bValue) {
                abc = "c";
              }
            }
          }
          row.item = {};
          row.item.id = row.itemId;
          row.item.itemCd = row.itemCd;
          row.item.name = row.itemName;
          row.item.rank = count;
          row.item.ratio = row.ratio;
          row.item.cv = row.cv;
          row.item.total = row.total;
          row.item.lastSalesDate = row.lastSalesDate;
          row.item.custHrchyCd = row.custHrchyCd;
          row.item.custHrchyName = row.custHrchyName;
          row.item.bfFlg = row.bfFlg;
          row.item.dpFlg = row.dpFlg;

          if (row.total <= 0) {
            grade =  "cz";
            row.item.grade = grade;
            row.item.invClassCd = grade.toUpperCase();
            czClass.push({...row.item});
          } else {
            if (row.cv <= xValue) {
              grade = `${abc}x`;
              row.item.grade = grade;
              row.item.invClassCd = grade.toUpperCase();
              if (abc === "a") {
                axClass.push({...row.item});
              } else if (abc === "b") {
                bxClass.push({...row.item});
              } else {
                cxClass.push({...row.item});
              }
            } else if (row.cv > xValue && row.cv <= yValue) {
              grade = `${abc}y`;
              row.item.grade = grade;
              row.item.invClassCd = grade.toUpperCase();
              if (abc === "a") {
                ayClass.push({...row.item});
              } else if (abc === "b") {
                byClass.push({...row.item});
              } else {
                cyClass.push({...row.item});
              }
            } else if (row.cv > yValue) {
              grade = `${abc}z`;
              row.item.grade = grade;
              row.item.invClassCd = grade.toUpperCase();
              if (abc === "a") {
                azClass.push({...row.item});
              } else if (abc === "b") {
                bzClass.push({...row.item});
              } else {
                czClass.push({...row.item});
              }
            }
          }
          row.rank = count;
          row.grade = grade;
          row.item.beforetotalAccu = beforetotalAccu;
          beforeValueTotal = row.total;
        }
      }

      allClass.push(
        ...axClass,
        ...bxClass,
        ...cxClass,
        ...ayClass,
        ...byClass,
        ...cyClass,
        ...azClass,
        ...bzClass,
        ...czClass
      );
      allClass.sort((a, b) => b.ratio - a.ratio);
      const result = {
        AX: axClass,
        BX: bxClass,
        CX: cxClass,
        AY: ayClass,
        BY: byClass,
        CY: cyClass,
        AZ: azClass,
        BZ: bzClass,
        CZ: czClass,
      };
      setAllData(allClass);
      setAbcXyzCnt(result);

      if (reduxViewState) {
          reduxViewState.abcXyzCnt = result;
          reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState }));
      }

    }
  };

  const handleSearch = (start, end, toggle, custLevelCd) => {
    onBtShowLoading();
    //reset
    bfUpdate.current = new Map();
    dpUpdate.current = new Map();
    bfTargetService
      .get({startDate: start, endDate: end, type: toggle, custLevelCd})
      .then(response => {
        if (response.data) {
          setResultData(response.data.abcXyz);
          computeAbcXyz(response.data.abcXyz);
          makeHeatMapData(response.data.abcXyz);
          currencyCd.current = response.data?.symbol;
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t("MSG_ServerError") });
        debug(e);
      })
      .then(() => {
        const reduxObjState = reduxUtil.createObjectState(viewName, {
          startDate: start, endDate: end, type: toggle, userRoles, custLevel, custLevelDatas, maxCv
        });
        reduxDispatch(reduxUtil.updateViewState({ ...reduxObjState }));
        setIsLoading(false)
        setReload(false)
        onBtHide();
      });
  };

  const saveConfig = () => {
    const format = "yyyy-MM-dd";
    const objVal = {
      periodStart: dateUtils.formatDate(dateUtils.startOfMonthDate(startDate), format),
      periodEnd: dateUtils.formatDate(dateUtils.endOfMonthDate(endDate), format),
      maxCv,
      custLevelId: custLevel?.id,
      abcBase: type,
      abcMin: gradeA,
      abcMax: gradeB,
      xyzMin: gradeX,
      xyzMax: gradeY
    }

    if (isEmpty(abcConfig)) {
      abcConfig.moduleCd = "DP";
      abcConfig.activeFlg = true;
      abcConfig.configCd = ABC_XYZ;
    }
    abcConfig.objVal = objVal;

    planConfigService.upsert([{...abcConfig}])
    .then(() => {
      setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
      initialPlanConfig();
      setFinishSave(false);
    })
    .catch(e => {
      setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
      setFinishSave(false);
      debug(e);
    })
  }

  const saveCustomerItem = () => {
    const bfUpdateSet = [];
    const dpUpdateSet = [];
    if (!isEmpty(bfUpdate?.current)) {
      for (const data of bfUpdate.current.values()) {
        if (data.bfFlg !== data.initialFlg) {
          bfUpdateSet.push(data);
        }
      }
    }

    if (!isEmpty(dpUpdate?.current)) {
      for (const data of dpUpdate.current.values()) {
        if (data.dpFlg !== data.initialdpFlg) {
          dpUpdateSet.push(data);
        }
      }
    }
    if (allData.length > 0) {
      const saveDatas = [];
      for (const data of allData) {
        const baseData = {
          itemCd: data.itemCd,
          itemId: data.id,
          customerCd: data.custHrchyCd,
          invClassCd: data.invClassCd
        };
        //BF UPDATE에 해당 ITEM CUSTOMER HRCHY가 존재하면 dto에 bf_update_flg = true
        if (bfUpdateSet.findIndex(row => row.itemCd === data.itemCd && row.custHrchyCd === data.custHrchyCd) > -1) {
          baseData.bfUpdateFlg = true;
          baseData.bfFlg = data.bfFlg;
        }
        if (dpUpdateSet.findIndex(row => row.itemCd === data.itemCd && row.custHrchyCd === data.custHrchyCd) > -1) {
          baseData.dpUpdateFlg = true;
          baseData.dpFlg = data.dpFlg;
        }
        saveDatas.push(baseData);
      }
      bfTargetService.upsert([...saveDatas])
        .then(() => {
          saveConfig();
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          setFinishSave(false);
          debug(e);
        })
        .then(() => {
          setReload(true);
          // handleSearch(startDate, endDate, type);
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      setFinishSave(false);
    }
  };

  const handleDialog = action => {
    if (action === "SAVE") {
      if (!dateUtils.dateValidation(new ZDate(startDate)) || !dateUtils.dateValidation(new ZDate(endDate))) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.warning, content: t("MSG_NoDate") });
        setFinishSave(false)
        return;
      }
      if (!maxCv) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.warning, content: t("MSG_MaxCv") });
        setFinishSave(false)
        return;
      }
      // setFinishSave(true);
      saveCustomerItem();
    } else {
      setFinishSave(false);
    }
  };

  const setColDef = () => {
    const combinedColDefs = includes(userRoles, roles.APP_BF) ? [...bfColDef, ...colDefs] : colDefs
    GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: combinedColDefs, viewName, initState: true });
    setGridLoading(true);
  }

  const onGridReady = params => {
    DataState.initialize(params.api);
  };

  const onChangeAllChecked = isChecked => {
    if (isChecked) {
      const cloneCodes = cloneDeep(codes);
      setABCXYZ(cloneCodes);
      setSelectItemData(cloneCodes);
      if (reduxViewState) {
        reduxViewState.abcxyz = cloneCodes;
      }
    } else {
      setABCXYZ([]);
      setSelectItemData([]);
      if (reduxViewState) {
        reduxViewState.abcxyz = [];
      }
    }
    setIsAllChecked(isChecked);

    if (reduxViewState) {
      reduxViewState.isAllChecked = isChecked;
      reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState }));
    }
  };

  const onChangeType = (value) => {
    setType(value);
  }

  const retrieveUserRoles = () => {
    userService.getUserAccessRoles({ username: userData.username })
      .then(
        (response) => {
          const userAccess = response.data;
          setUserRoles(userAccess?.roles);
        },
      )
      .catch(e => { debug(e); });
  };

  const retrieveCustLevel = () => {
    hrchyConfigService.getAll({ searchFilters: { hrchyNum: codeUtil.hrchyNum.HRCHY } })
      .then(response => {
        const custConfig = [];
        if (response.data?.results) {
          const result = response.data.results;
          result.sort((a,b) => {
            if (a.seqNum < b.seqNum) {
                return -1;
            }
            if (a.seqNum > b.seqNum) {
                return 1;
            }
            return 0;
          });
          for (const row of result) {
            if (!row.itemHrchyFlg) {
              custConfig.push(row);
            }
          }
          setCustLevelDatas([...custConfig]);
          setCustLevel(custConfig[0]);
        }
      })
      .catch(e => {
        debug(e);
      })
  };

  useEffect(() => {
    if (reload) {
      handleSearch(startDate, endDate, type, custLevel?.cd)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload])

  useEffect(() => {
    if (!isLoading) {
      /* type is [Quantity, Revenue, Frequency, Profit] */
      // handleSearch(startDate, endDate, type)
      setReload(true)

      if (gridLoading) {
        const combinedColDefs = includes(userRoles, roles.APP_BF) ? [...bfColDef, ...colDefs] : colDefs

        GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: combinedColDefs, viewName, initState: true });
      }

      if (reduxViewState) {
        reduxViewState.type = type;
        reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  useEffect(() => {
    if (!isLoading) {
      if (maxCv !== null && maxCv >= 2 && maxCv <= 4 && resultData) {
        makeHeatMapData(resultData)
      } else {
        setSnackInfo({ open: true, severity: snackBarMessageColor.warning, content: t("MSG_MaxCv")});
        return;
      }
      if (reduxViewState) {
        reduxViewState.maxCv = maxCv;
        reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState }));
      }
    }
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxCv])

  useEffect(() => {
    const rowData = [];
    if (gridLoading) {
      gridRef.current.api.forEachNode(node => {
        if (node.rowIndex !== null) {
          const rowNodeData = node.data;
          if (rowNodeData.bfFlg === null || rowNodeData.bfFlg === !selectAll) {
            rowNodeData.bfFlg = selectAll;
            node.setDataValue("bfFlg", selectAll);
            rowData.push(rowNodeData);
          }
        }
      });
      const updatedRow = gridRef.current.api.applyTransaction({ update: rowData }).update;
      for (const row of updatedRow) {
        if (row.data.bfFlg === row.data.initialFlg) {
          delete row.rowState;
        } else {
          row.rowState = 'updated';
          DataState.setDataState(row);
          bfUpdate.current.set(row.data.custHrchyCd+row.data.itemCd, {
            ...row.data
          });
        }
      }
      gridRef.current.api.redrawRows({rowNodes: updatedRow});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectAll]);

  useEffect(() => {
    const rowData = [];
    if (gridLoading) {
      gridRef.current.api.forEachNode(node => {
        if (node.rowIndex !== null) {
          const rowNodeData = node.data;
          if (rowNodeData.dpFlg === null || rowNodeData.dpFlg === !selectDpAll) {
            rowNodeData.dpFlg = selectDpAll;
            node.setDataValue("dpFlg", selectDpAll);
            rowData.push(rowNodeData);
          }
        }
      });
      const updatedRow = gridRef.current.api.applyTransaction({ update: rowData }).update;
      for (const row of updatedRow) {
        if (row.data.dpFlg === row.data.initialdpFlg) {
          delete row.rowState;
        } else {
          row.rowState = 'updated';
          DataState.setDataState(row);
          dpUpdate.current.set(row.data.custHrchyCd+row.data.itemCd, {
            ...row.data
          });
        }
      }
      gridRef.current.api.redrawRows({rowNodes: updatedRow});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectDpAll]);

  useEffect(() => {
    if (abcValues.length > 0 && Object.keys(resultData)?.length > 0) {
      computeAbcXyz(resultData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gradeA, gradeB]);

  useEffect(() => {
    if (xyzValues.length > 0 && Object.keys(resultData)?.length > 0) {
      computeAbcXyz(resultData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gradeX, gradeY]);

  useEffect(() => {
    if (Object.keys(abcConfig).length <= 0 || !isLoading) {
      if (reduxViewState?.startDate) {
        if (isLoading) {
          handleSearch(reduxViewState.startDate, reduxViewState.endDate, type, custLevel?.cd);
        }
      } else {
        if (startDate && new ZDate(startDate) && isLoading && type) {
          setEndDate(dateUtils.endOfMonthDate(endDate));
          // handleSearch(startDate, endDate, type);
          setReload(true)
        }
      }
    }

    if (size(reduxViewState?.abcxyz ?? []) < 1) {
      setABCXYZ([DEFAULT_ACTIVE]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate]);

  useEffect(() => {
    if (!isEmpty(userRoles)) {
      setColDef();
      if (gridLoading) {
        initialPlanConfig();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRoles, gridLoading]);

  useEffect(() => {
    if (isEmpty(userRoles)) {
      retrieveUserRoles();
      retrieveCustLevel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (Object.keys(abcConfig).length > 0 && isLoading) {
      const objVal = abcConfig.objVal;
      if (!isEmpty(objVal)) {
        if (!objVal?.periodStart) {
          const date = new ZDate(new ZDate(endDate).setFullYear(new ZDate(endDate).getFullYear() - 1));
          objVal.periodStart =  dateUtils.startOfMonthDate(new ZDate(date.setDate(date.getDate() + 1)))
        }
        setStartDate(new ZDate(objVal?.periodStart));
        if (!objVal?.periodEnd) {
          objVal.periodEnd = dateUtils.endOfMonthDate(endDate);
        }
        setEndDate(new ZDate(objVal?.periodEnd));
        onChangeType(objVal?.abcBase);
        const cv = typeof objVal?.maxCv === 'string' ? Number(objVal?.maxCv) : objVal?.maxCv;
        const ga = typeof objVal?.abcMin === 'string' ? Number(objVal?.abcMin) : objVal?.abcMin
        const gb = typeof objVal?.abcMax === 'string' ? Number(objVal?.abcMax) : objVal?.abcMax
        const gx = typeof objVal?.xyzMin === 'string' ? Number(objVal?.xyzMin) : objVal?.xyzMin;
        const gy = typeof objVal?.xyzMax === 'string' ? Number(objVal?.xyzMax) : objVal?.xyzMax

        setMaxCv(cv)
        setGradeA(ga);
        setGradeB(gb);
        setGradeX(gx);
        setGradeY(gy);
        setXyzValues([gx, gy])
        setAbcValues([-gb, -ga])
        const custObj = custLevelDatas.find(row => row.id === objVal?.custLevelId);
        if (custObj == null || isEmpty(custObj)) {
          setCustLevel(custLevelDatas[0]);
        } else {
          setCustLevel(custObj);
        }
        abcXyzValues.current = {xyzValues: [gx, gy], abcValues: [-gb, -ga]}
        setReload(true)

        if (reduxViewState) {
          reduxViewState.startDate = new ZDate(objVal?.periodStart);
          reduxViewState.endDate = new ZDate(objVal?.periodEnd);
          reduxViewState.abcBase = objVal?.type;
          reduxViewState.maxCv = cv;
          reduxViewState.gradeA = ga;
          reduxViewState.gradeB = gb;
          reduxViewState.gradeX = gx;
          reduxViewState.gradeY = gy;
          reduxViewState.custLevel = custObj;
          reduxViewState.xyzValues = [gx, gy];
          reduxViewState.abcValues = [-gb, -ga];
          reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState }));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abcConfig])

  useEffect(() => {
    if (size(abcXyzCnt) > 0 && abcxyz.length > 0) {
      setSelectItemData(abcxyz);
    } else if (abcxyz.length === 0) {
      setSelectItemData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abcXyzCnt, abcxyz]);

  useEffect(() => {
    if (endDate) {
      const date = new ZDate(endDate);
      date.setFullYear(date.getFullYear() - minDateYear);
      setStartMinDate(date)
    }
 // eslint-disable-next-line react-hooks/exhaustive-deps
  },[endDate])

  useEffect(() => {
    if (size(gridDatas) > 0) {
      setColDef();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridDatas]);

  const handleChange = (event) => {
    const selectedCustLevel = custLevelDatas.find(row => String(row.id) === event.target.value);
    setCustLevel(selectedCustLevel);
    setReload(true)
  }

  return (
    <Box
      id="abc-xyz-analysis" sx={{ display: "flex", flexDirection: "column", height: "100%" }}
      className={"clickable dp"}>
        <Backdrop
          sx={{ color: "#fff", zIndex: theme => theme.zIndex.drawer + 1 }}
          open={finishSave}>
          <CircularProgress color="inherit" />
        </Backdrop>
        <FilterContainer>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t("periodStart")}
              views={["year", "month"]}
              format={"yyyy-MM"}
              value={startDate}
              maxDate={endDate}
              minDate={startMinDate}
              openTo={"month"}
              onAccept={newStartValue => {
                setStartDate(dateUtils.startOfMonthDate(newStartValue));
                setReload(true);
                // handleSearch(newStartValue, endDate, type);
              }}
              slotProps={{ textField: { size: "small", sx: { mr: 1.5, width: 130 } } }}
            />
            <DatePicker
              label={t("periodEnd")}
              views={["year", "month"]}
              format={"yyyy-MM"}
              value={endDate}
              minDate={startDate ? startDate : startMinDate}
              openTo={"month"}
              maxDate={dateUtils.endOfMonthDate(new ZDate())}
              onAccept={newEndValue => {
                const end = dateUtils.endOfMonthDate(newEndValue);
                setEndDate(end);
                // handleSearch(startDate, end, type);
                setReload(true)
              }}
              slotProps={{ textField: { size: "small", sx: { mr: 1.5, width: 130 } } }}
            />
          </LocalizationProvider>
          <FormControl size="small" sx={{ mr: 1.5 }} style={{ width: 100 }} >
            <TextField
              label={t("maxCv")}
              size="small"
              name="maxCv"
              value={maxCv ?? defaultCv}
              type="number"
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength:3 , min: 2, max: 4, step: 0.05 }}
              sx={{ textAlign: "center" }}
              onKeyDown={(e) => {
                // 화살표 위/아래는 허용하고 나머지 키 입력은 차단
                if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') {
                  e.preventDefault();
                }
              }}
              onChange={(event) => { setMaxCv(event.target.value) }}
            />
          </FormControl>
          <FormControl size="small" sx={{ mr: 1.5 }} style={{ minWidth: 200 }} >
            <InputLabel>ABC-XYZ</InputLabel>
            <Select
              size="small"
              label="ABC-XYZ"
              value={type}
              onChange={(event) => { onChangeType(event.target.value) }}
            >
              <MenuItem value={REVENUE} key={REVENUE}>{t(REVENUE)}</MenuItem>
              <MenuItem value={QUANTITY} key={ QUANTITY}>{t(QUANTITY)}</MenuItem>
              <MenuItem value={FREQUENCY} key={FREQUENCY}>{t(FREQUENCY)}</MenuItem>
              <MenuItem value={PROFIT} key={PROFIT}>{t(PROFIT)}</MenuItem>
              <MenuItem value={COGS} key={COGS}>{t(COGS)}</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ mr: 1.5 }} style={{ minWidth: 300 }} >
            <InputLabel>{t("custLevel")}</InputLabel>
            <Select
              size="small"
              label={t("custLevel")}
              value={String(custLevel?.id ?? '')}
              onChange={(event) => { handleChange(event) }}
            >
              {custLevelDatas?.map((cust) => {
                return <MenuItem value={String(cust.id)} key={cust.id}>{GridUtils.gridValueL10N(cust.name)}</MenuItem>
              })}
            </Select>
          </FormControl>
          <Box sx={{ float: "right" }}>
            <SaveButton
              onClick={() => {
                setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") });
              }}
            />
          </Box>
        </FilterContainer>
        <Box sx={{ display: "flex", width: "100%", height: "60%", p: "20px 0", boxSizing: "border-box" }}>
          <Box
            sx={{ position: "relative", width: "50%", height: "100%", mt: -2, pl: 8, pb: 5, boxSizing: "border-box" }}>
            <Box sx={{ position: "absolute", height: "calc(100% - 125px)", top: 45, left: 0, ml: 5 }}>
              <Slider
                getAriaLabel={() => "Minimum distance shift"}
                sx={{ display: "inline-block", height: "100% " }}
                value={abcValues}
                orientation="vertical"
                onChangeCommitted={gradeAbcChangeCommitted}
                onChange={gradeAbcChange}
                valueLabelDisplay="on"
                valueLabelFormat={v => Math.abs(v)}
                disableSwap
                step={5}
                min={-95}
                max={-5}
                marks
              />
            </Box>
            { maxCv !== null && (<HeatMap
              name="heatMapQTY"
              size={["100%", "100%"]}
              targetCol="fill_rate"
              yAxisName={type}
              selectHeatMap={getItemOnHeatMap}
              gradeA={Math.abs(gradeA)}
              gradeB={Math.abs(gradeB)}
              gradeX={gradeX}
              gradeY={gradeY}
              mapData={heatMapData}
              maxCv={maxCv ?? defaultCv}
            />
            )}
            <Box sx={{ position: "absolute", width: "calc(100% - 140px)", bottom: -20, left: 125 }}>
            { maxCv !== null && ( <Slider
                getAriaLabel={() => "Minimum distance shift"}
                sx={{ display: "inline-block" }}
                value={xyzValues}
                onChangeCommitted={gradeXyzChangeCommitted}
                onChange={gradeXyzChange}
                valueLabelDisplay="on"
                step={interval}
                disableSwap
                min={interval}
                max={maxCv ? Number((maxCv-interval).toFixed(digit)) : 1.95}
                marks
              />
            )}
            </Box>
          </Box>
          <Box sx={{ position: "relative", width: "50%", mt: 2 }}>
            <Box sx={{ position: "absolute", top: -37, left: 0, pl: "120px" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isAllChecked}
                    onChange={e => {
                      onChangeAllChecked(e.target.checked);
                    }}
                  />
                }
                label={t("all")}
              />
            </Box>
            <AbcXyzContentArea
              t={t}
              ITEM={ITEM}
              data={abcXyzCnt}
              currencyCd={currencyCd}
              type={type}
              abcxyz={abcxyz}
              userData={userData}
              axClick={axClick}
              yAxisName="heatMapQTY"
            />
          </Box>
        </Box>
        <Box
          className="ag-theme-balham"
          sx={{ width: "100%", height: "calc(40% - 63px)" }}>
          <AgGridReact
            ref={gridRef}
            rowData={gridDatas}
            {...defaultGridMemo}
            onGridReady={onGridReady}
            overlayLoadingTemplate={`<span class="ag-overlay-loading-center">${t("MSG_Loading")}</span>`}
            pagination
            paginationPageSize={15}
            paginationPageSizeSelector={[15, 20, 50, 100]}
          />
        </Box>
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </Box>
  );
};

export default withTranslation()(TargetItemSelection);
